import React from "react";

export default function Manual() {
  const sections = [
    { id: "overview", title: "System Overview" },
    { id: "roles", title: "User Roles & Access" },
    { id: "features", title: "Key Features" },
    { id: "uniforms", title: "Uniforms Module" },
    { id: "finance", title: "Finance Module" },
    { id: "sis", title: "SIS Module" },
    { id: "portal", title: "Student Portal" },
    { id: "howto", title: "Step-by-Step Guides" },
    { id: "troubleshooting", title: "Troubleshooting" },
    { id: "roadmap", title: "Roadmap" },
    { id: "technical", title: "Technical Documentation" },
    { id: "deploy", title: "Build & Deploy" },
    { id: "credits", title: "Credits & Ownership" },
    { id: "support", title: "Support & Maintenance" }
  ];
  function Print() {
    window.print();
  }
  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">VMSC-SMS Handbook</h3>
        <button className="btn btn-primary" onClick={Print}>Print PDF</button>
      </div>
      <p className="text-muted">Village Montessori School Management System • User + Technical Guide</p>
      <div className="card p-3 mb-3">
        <div className="row g-2">
          {sections.map(s => (
            <div key={s.id} className="col-md-3">
              <a className="btn btn-outline-secondary w-100" href={`#${s.id}`}>{s.title}</a>
            </div>
          ))}
        </div>
      </div>

      <section id="overview" className="mb-4">
        <h5>System Overview</h5>
        <ul>
          <li>Name: Village Montessori School Management System (VMSC-SMS)</li>
          <li>Modules: Finance, SIS, HR/Payroll, Uniforms, Books, Student Portal</li>
          <li>Access: Staff login for back office; Google sign-in for Student Portal</li>
        </ul>
      </section>

      <section id="roles" className="mb-4">
        <h5>User Roles & Access</h5>
        <ul>
          <li>Admin/Superadmin: Configuration, pricing, sections, data tools, full access</li>
          <li>Finance: Payments, SOA, cash flow, ledgers, reports</li>
          <li>Registrar: Enrollment edit/print, students, subjects, COR, SIS Finance linking</li>
          <li>Uniforms: Inventory, Sales, Products</li>
          <li>Books: Inventory, Bundles, Sales, Reports</li>
          <li>HR: Payroll dashboard, employees, reports</li>
          <li>Student/Parent: Student Portal (Google sign-in) with finance view and SOA</li>
        </ul>
      </section>

      <section id="features" className="mb-4">
        <h5>Key Features</h5>
        <ul>
          <li>Finance: Batch SOA, campus filters, payment modes, tuition ledger</li>
          <li>SIS: Structured Enrollment Address (Current/Perm), Enrollment Form print</li>
          <li>Uniforms: Inventory CSV import, SKU/barcode, POS-like Sales</li>
          <li>Student Portal: Google sign-in, finance cards, payments history, SOA</li>
        </ul>
      </section>

      <section id="uniforms" className="mb-4">
        <h5>Uniforms Module</h5>
        <p>Tabs: Inventory, Sales, Products</p>
        <ul>
          <li>Inventory: Manage stock; columns Uniform Name, Size, SKU, Barcode, Price, Cost, On Hand, Adjust</li>
          <li>CSV headers: uniformName, size, sku, barcode, price, cost, onHand</li>
          <li>Sales: Scan barcode or select SKU; On Hand decreases automatically</li>
          <li>Products: Define items, sizes, SKUs, barcodes; enforce consistent patterns</li>
        </ul>
      </section>

      <section id="finance" className="mb-4">
        <h5>Finance Module</h5>
        <ul>
          <li>Record payments in Payments or SIS Finance; set studentId exactly (e.g., S-GAPAN-0002)</li>
          <li>Financial Reports → SOA: Select Campus, Payment Mode, Target Month; “Open PDF in new tab” to preview</li>
          <li>SIS Finance tools: Link OR to student; link name+campus matching payments</li>
        </ul>
      </section>

      <section id="sis" className="mb-4">
        <h5>SIS Module</h5>
        <ul>
          <li>Enrollment (Edit): Current & Permanent Address blocks; checkbox to autofill</li>
          <li>Enrollment Form Print: DepEd-style form with per-field address boxes</li>
          <li>Students, Subjects, COR; SIS Finance for registrar</li>
        </ul>
      </section>

      <section id="portal" className="mb-4">
        <h5>Student Portal</h5>
        <ul>
          <li>Sign in with Google; auto-link by student.email or guardianEmail; “Switch student” to re-link</li>
          <li>Cards: Total Due/Paid/Outstanding/Next Due; Payments; Announcements; Grades; SOA</li>
        </ul>
      </section>

      <section id="howto" className="mb-4">
        <h5>Step-by-Step Guides</h5>
        <ul>
          <li>Staff Login: /login; ensure role registrar/admin/finance</li>
          <li>Student Portal: /portal; ensure domain is authorized in Firebase Auth</li>
          <li>Enrollment Edit: Fill structured address; check autofill; Save; Print from Enrollment Form</li>
          <li>Batch SOA: Financial Reports → SOA; choose Campus, Mode, Month; Generate; preview tab</li>
          <li>Uniforms CSV: Prepare headers; import to Inventory; verify SKUs and On Hand</li>
        </ul>
      </section>

      <section id="troubleshooting" className="mb-4">
        <h5>Troubleshooting</h5>
        <ul>
          <li>Redirect to landing: sign in at /login; use ?v=20260327 to refresh bundle</li>
          <li>Google popup closes: add portal domain to Firebase Auth Authorized domains</li>
          <li>Payments missing: legacy student_id vs studentId; use SIS Finance linking tools</li>
          <li>Blank print: sign in; cache-buster; print page now loads from Firestore</li>
        </ul>
      </section>

      <section id="roadmap" className="mb-4">
        <h5>Roadmap</h5>
        <ul>
          <li>Server auth with JWT; /api/me; staff Google sign-in</li>
          <li>Grades backend and registrar entry; richer student grades UI</li>
          <li>Announcements admin; scheduling and visibility</li>
          <li>Student documents upload/view</li>
          <li>Fees ledger API and SOA endpoint</li>
          <li>Uniforms restock form, CSV template, low-stock alerts, margin column</li>
          <li>Move expenses to Firestore for unified cash flow</li>
        </ul>
      </section>

      <section id="technical" className="mb-4">
        <h5>Technical Documentation</h5>
        <ul>
          <li>Frontend: React + React Router; modules under src/pages</li>
          <li>Data: Firestore collections (students, payments, grades, announcements)</li>
          <li>Normalization: payments accept studentId or student_id; campus lowercase</li>
          <li>Print styles: per-module CSS with @media print and page sizing</li>
          <li>Batch SOA: jsPDF legal format; per-page card layout adapts to count</li>
          <li>Portal: Firebase Auth (Google); local mapping stored per user; switch student button</li>
        </ul>
      </section>

      <section id="deploy" className="mb-4">
        <h5>Build & Deploy</h5>
        <ul>
          <li>Build: npm run build</li>
          <li>Deploy: firebase deploy --only hosting</li>
          <li>Cache bust: append ?v=20260327 to force fresh bundle</li>
          <li>Auth domains: add portal and hosting domains in Firebase Auth</li>
        </ul>
      </section>

      <section id="credits" className="mb-4">
        <h5>Credits & Ownership</h5>
        <ul>
          <li>System Creator: Warlie Castro (Village Montessori School)</li>
          <li>Product Name: Village Montessori School Management System (VMSC-SMS)</li>
          <li>Copyright: © {new Date().getFullYear()} Village Montessori School</li>
        </ul>
      </section>

      <section id="support" className="mb-4">
        <h5>Support & Maintenance</h5>
        <ul>
          <li>Channels: Email and chat support for staff (registrar, finance, admin)</li>
          <li>Coverage: Bug fixes, operational issues, minor UI tweaks, and data corrections</li>
          <li>Service levels:
            <ul>
              <li>Critical (production down): response within 4 business hours; hotfix same day</li>
              <li>High (core feature broken): response within 1 business day; fix 1–3 days</li>
              <li>Normal (minor defect/UX): scheduled for next maintenance release</li>
            </ul>
          </li>
          <li>Release cadence: Maintenance as needed; monthly improvements</li>
          <li>Backup & continuity: Source code under version control; Firebase backups; documented deployment steps</li>
        </ul>
      </section>

      

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .btn, .card .btn { display: none !important; }
          a[href] { text-decoration: none; color: #000; }
          h3, h5 { margin-top: 0; }
          .container { padding: 0; }
        }
      `}</style>
    </div>
  );
}
